/**
 * Deposit's content (the design system's `DepositModal`): the top-up packs (`balancePurchases`),
 * Stripe's checkout and its return, and POST /v1/me/gift-codes/redeem. A payment confirmed or a
 * code redeemed lands as funds added, held out of the chrome's balance (`uncollectedFunds.ts`)
 * until the dialog closes and its trail reaches the balance pill. The open state and the URL's
 * requests are `lib/deposit.ts`; the modal is `components/StoreDeposit.tsx`.
 */
import { type Currency, capitalizeTerm, formatAmount, USD } from "@loot-os/api-contract";
import type {
  DepositCardState,
  DepositModalProps,
  DepositResult,
  DepositWords,
  GiftCodeField,
} from "@loot-os/design-system/domain/DepositModal";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { track } from "./analytics.js";
import { useCurrency } from "./currency.js";
import {
  depositRequestOf,
  setDepositOpen,
  useDepositOpen,
  withoutDepositRequest,
} from "./deposit.js";
import { giftCodeErrorMessage, useRedeemGiftCode } from "./giftCodes.js";
import {
  balancePacksQueryOptions,
  balanceQueryOptions,
  lootMeQueryOptions,
  PURCHASE_POLL_LIMIT,
  purchasePollQueryOptions,
} from "./queries.js";
import { queryClient } from "./queryClient.js";
import { CheckoutError, type CheckoutPurchase, useShopper, useStartCheckout } from "./shopper.js";
import { useTenant } from "./tenant.js";
import { showToast } from "./toasts.js";
import { collectFunds, holdBalanceAt } from "./uncollectedFunds.js";

/** Funds added, before the dialog turns them into its result (with the collect callback). */
interface Added {
  cents: number;
  from: string;
  balanceCents: number | null;
}

/** Where a checkout that came back stands. */
type PurchaseState =
  | { status: "confirming" }
  | ({ status: "completed" } & Added)
  /** Stripe or the bank refused it, or it was refunded or disputed before it settled. */
  | { status: "failed" }
  /** Still pending after the poll limit: it may settle yet. */
  | { status: "unconfirmed" }
  | { status: "canceled" };

/** Deposit, open or closed, filled for the signed-in shopper. */
export function useDepositModalContent(): DepositModalProps {
  const open = useDepositOpen();
  const { sessionToken } = useShopper();
  const { hasFeature, config } = useTenant();
  const currency = useCurrency();
  const words = depositWords(currency);
  const unit = currency.namePlural;
  // GET /v1/me carries the lock; the server refuses balance purchases and redemptions on its own,
  // so this is the shopper-facing half of the same state.
  const me = useQuery({ ...lootMeQueryOptions(), enabled: !!sessionToken && open }).data ?? null;
  const locked = me?.locked ?? false;
  const [error, setError] = useState<string | null>(null);

  const purchase = usePurchaseReturn(open, words.methods.card);
  // However the dialog closed, funds it held back reach the chrome: the trail collects them as its
  // first mark lands (well inside this), and this catches a close that ran no trail.
  useEffect(() => {
    if (open) return;
    const timer = window.setTimeout(collectFunds, COLLECT_BY_MS);
    return () => window.clearTimeout(timer);
  }, [open]);
  const gift = useGiftCode(locked);

  const canBuy = hasFeature("balancePurchases");
  // The store's bundles: offers beside a deposit. None (or a failed read) leaves deposits alone.
  const bundles = useQuery({
    ...balancePacksQueryOptions(),
    enabled: canBuy && !!sessionToken && open,
  });
  const checkout = useStartCheckout();
  const startCheckout = (purchase: CheckoutPurchase) => {
    setError(null);
    checkout.mutate(purchase, { onError: (err) => setError(checkoutErrorMessage(err)) });
  };

  const deposit: DepositCardState = !canBuy
    ? {
        status: "unavailable",
        message: `Buying ${unit} isn't available in this store. You can still redeem a gift code.`,
      }
    : bundles.isPending
      ? { status: "loading" }
      : {
          status: "ready",
          ...config.deposit,
          bundles: bundles.data ?? [],
          onDeposit: (amountCents) => startCheckout({ amountCents }),
          onBuyBundle: (packId) => startCheckout({ packId }),
          // Through the redirect: the page is leaving.
          buying: checkout.isPending || checkout.isSuccess,
          disabled: locked || purchase.state?.status === "confirming",
        };

  const notices: DepositModalProps["notices"] = [
    ...(locked
      ? [
          {
            id: "locked",
            tone: "danger" as const,
            text: `This account is locked, so you can't buy ${unit} or redeem codes. Contact support to get it lifted.`,
          },
        ]
      : []),
    ...(error ? [{ id: "error", tone: "danger" as const, text: error }] : []),
    ...(purchase.state?.status === "canceled"
      ? [{ id: "purchase", tone: "neutral" as const, text: "Checkout canceled." }]
      : []),
  ];

  const added: Added | null =
    gift.added ?? (purchase.state?.status === "completed" ? purchase.state : null);
  const result: DepositResult | null = added
    ? {
        status: "added",
        cents: added.cents,
        from: added.from,
        balanceCents: added.balanceCents,
        onCollected: collectFunds,
      }
    : purchase.state?.status === "confirming"
      ? { status: "processing" }
      : purchase.state?.status === "failed"
        ? {
            status: "failed",
            title: FAILED_TITLE,
            body: FAILED_BODY,
            onRetry: purchase.dismiss,
          }
        : purchase.state?.status === "unconfirmed"
          ? { status: "failed", title: "Still confirming your payment", body: UNCONFIRMED_TEXT }
          : null;

  return {
    open,
    onOpenChange: (next) => {
      setDepositOpen(next);
      if (!next) {
        setError(null);
        purchase.dismiss();
        gift.reset();
      }
    },
    currency,
    words,
    notices,
    deposit,
    giftCode: gift.field,
    result,
  };
}

/** Why checkout didn't start, as the dialog says it. */
function checkoutErrorMessage(err: Error): string {
  switch (err.message) {
    case "account_locked":
      return "This account is locked. Contact support.";
    case "fraud_declined":
      return "This purchase could not be completed. Contact support.";
    case "deposit_amount_out_of_range": {
      // The limits checkout read, which a config cached before a change may not show yet.
      const { minCents, maxCents } = err instanceof CheckoutError ? err.limits : {};
      return minCents !== undefined && maxCents !== undefined
        ? `Deposits here are ${formatAmount(minCents, USD)} to ${formatAmount(maxCents, USD)}. Pick another amount.`
        : "That amount is outside what this store takes. Pick another.";
    }
    default:
      return "Could not start checkout. Try again.";
  }
}

/** How long after Deposit closes the chrome shows held-back funds at the latest (ms). */
const COLLECT_BY_MS = 2000;

const FAILED_TITLE = "Payment didn't go through";
const FAILED_BODY = "Nothing was added to your balance. Try again, or pay another way.";

const UNCONFIRMED_TEXT =
  "We couldn't confirm the purchase yet. If you paid, the balance updates as soon as the payment settles.";

/** The dialog's words: platform words, the same on every store but for its currency's name. */
function depositWords(currency: Currency): DepositWords {
  return {
    title: "Deposit",
    close: "Close",
    back: "Back",
    methodLabel: "Payment method",
    methods: { card: "Card", gift: "Gift code" },
    amountsLabel: "Amount",
    amountLabel: "Amount in US dollars",
    youGet: "You get",
    minimum: (amount) => `Minimum ${amount}`,
    maximum: (amount) => `Maximum ${amount}`,
    bundlesLabel: "Bundles",
    bonus: (percent) => `${percent}% bonus`,
    keypad: { label: "Amount keypad", delete: "Delete, hold to clear", point: "Decimal point" },
    depositWithCard: "Deposit with card",
    buying: "Opening checkout",
    giftTitle: "Redeem a gift code",
    giftBody: "Its value goes straight into your balance.",
    giftLabel: "Gift code",
    giftPlaceholder: "XXXX-XXXX-XXXX-XXXX",
    giftSubmit: "Apply gift code",
    giftChecking: "Checking the gift code",
    processingTitle: "Processing your payment",
    processingBody: "This takes a few seconds.",
    addedTitle: `${capitalizeTerm(currency.namePlural)} added`,
    from: "From",
    balance: "Balance",
    done: "Done",
    retry: "Try again",
  };
}

/**
 * Funds just added, from the server's balance after them (`balanceCents` on the purchase, the
 * ledger row's balance after on a redemption): the spendable balance with them is that less what
 * unclaimed reveals hold, and the chrome holds at it less the funds until they're collected. The
 * balance query then refetches with them in it, which the chrome leaves out meanwhile.
 */
function landFunds(cents: number, from: string, balanceAfterCents: number | null): Added {
  const cached = queryClient.getQueryData(balanceQueryOptions().queryKey);
  const balanceCents =
    balanceAfterCents === null ? null : balanceAfterCents - (cached?.heldCents ?? 0);
  if (balanceCents !== null) holdBalanceAt(balanceCents - cents);
  void queryClient.invalidateQueries({ queryKey: balanceQueryOptions().queryKey });
  return { cents, from, balanceCents };
}

/**
 * What the URL asks of Deposit (`depositRequestOf`): `?deposit=1`, the old Wallet tab, or
 * Stripe's return to `/account?purchase=success&session_id=cs_...` (or cancelled). Each is read
 * once the session is known, then taken out of the URL (replace) so a reload or Back doesn't repeat
 * it; the ref keeps StrictMode's double effect from reading it twice. Signed out, the request is
 * dropped: there is nothing to add funds to. A paid return is polled until it leaves "pending"
 * (webhooks usually land within seconds), a 401, or the poll limit (purchasePollQueryOptions);
 * the outcome shows in the dialog, which can't be closed meanwhile, or as a toast if it was.
 */
function usePurchaseReturn(open: boolean, paidWith: string) {
  const { user, authPending, sessionToken } = useShopper();
  const navigate = useNavigate();
  const pathname = useLocation({ select: (location) => location.pathname });
  const searchStr = useLocation({ select: (location) => location.searchStr });
  const [state, setState] = useState<PurchaseState | null>(null);
  const [checkoutSessionId, setCheckoutSessionId] = useState<string | null>(null);
  const handledRef = useRef<string | null>(null);
  const openRef = useRef(open);
  openRef.current = open;

  useEffect(() => {
    const request = depositRequestOf(searchStr);
    if (!request || authPending) return;
    // Signed in, wait for the session token before acting on it (a checkout return polls with it).
    if (user && !sessionToken) return;
    const key = `${pathname}${searchStr}`;
    if (handledRef.current === key) return;
    handledRef.current = key;
    void navigate({ href: withoutDepositRequest(pathname, searchStr), replace: true });
    if (!user) return;
    setDepositOpen(true);
    const checkout = request.checkout;
    if (checkout?.status === "cancelled") {
      track("checkout_returned", { status: "cancelled" });
      setState({ status: "canceled" });
    } else if (checkout?.status === "success") {
      setState({ status: "confirming" });
      setCheckoutSessionId(checkout.sessionId);
    }
  }, [pathname, searchStr, authPending, user, sessionToken, navigate]);

  const poll = useQuery({
    ...purchasePollQueryOptions(checkoutSessionId ?? "none"),
    enabled: !!checkoutSessionId && state?.status === "confirming",
  });
  useEffect(() => {
    if (!checkoutSessionId || state?.status !== "confirming") return;
    const result = poll.data;
    if (!result) return;
    const terminal =
      result.kind === "unauthorized" ||
      (result.kind === "status" && result.status.status !== "pending");
    const timedOut = !terminal && result.attempt >= PURCHASE_POLL_LIMIT;
    if (!terminal && !timedOut) return;
    setCheckoutSessionId(null);
    const completed = result.kind === "status" && result.status.status === "completed";
    track("checkout_returned", { status: completed ? "completed" : "failed" });
    if (!openRef.current) {
      // Closed meanwhile: nothing to collect into, so the balance simply updates.
      setState(null);
      void queryClient.invalidateQueries({ queryKey: balanceQueryOptions().queryKey });
      showToast(
        completed
          ? { kind: "success", title: "Purchase complete." }
          : timedOut
            ? { kind: "error", title: UNCONFIRMED_TEXT }
            : { kind: "error", title: `${FAILED_TITLE}. ${FAILED_BODY}` },
      );
      return;
    }
    if (completed) {
      const { creditedCents, balanceCents } = result.status;
      setState({ status: "completed", ...landFunds(creditedCents, paidWith, balanceCents) });
    } else {
      // The balance may have moved even on the timeout path.
      void queryClient.invalidateQueries({ queryKey: balanceQueryOptions().queryKey });
      setState({ status: timedOut ? "unconfirmed" : "failed" });
    }
  }, [checkoutSessionId, state, poll.data, paidWith]);

  return {
    state,
    /** Back to choosing: an outcome already shown clears; a purchase still confirming stays. */
    dismiss: () => setState((current) => (current?.status === "confirming" ? current : null)),
  };
}

/**
 * The gift code field: POST /v1/me/gift-codes/redeem, then the funds added or why the code
 * didn't redeem. A refusal clears as soon as the shopper types again.
 */
function useGiftCode(disabled: boolean) {
  const redeem = useRedeemGiftCode();
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [added, setAdded] = useState<Added | null>(null);
  const field: GiftCodeField = {
    value: code,
    onChange: (value) => {
      setCode(value);
      setError(null);
    },
    onSubmit: () => {
      const typed = code.trim();
      if (!typed) return;
      setError(null);
      redeem.mutate(typed, {
        onSuccess: (redemption) => {
          setCode("");
          setAdded(
            landFunds(
              redemption.amountCents,
              `Gift code ${redemption.code}`,
              redemption.ledger.balanceAfterCents,
            ),
          );
        },
        onError: (err) => setError(giftCodeErrorMessage(err)),
      });
    },
    pending: redeem.isPending,
    disabled,
    error,
  };
  return {
    field,
    added,
    /** Closed: the next open starts on the field again. */
    reset: () => {
      setAdded(null);
      setError(null);
    },
  };
}
